import { NavigateFn, useNavigate } from '@tanstack/react-router'
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { normalizeString } from 'components/table/enhancements/filters/filterUtils'
import { TableComponent } from 'components/table/TableComponent'
import { compareAsc, parseISO } from 'date-fns'
import { WebGLContext } from 'features/assetDatabase/contexts/WebGLLayer'
import { useGetCustomSiteField } from 'features/assetDatabase/hooks/useGetCustomSideField'
import { AssetData } from 'features/assetDatabase/types/assetTableTypes'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import AssetTableColumns, { AssetTableColumn } from './AssetTableColumns'
import { getAssetTableCustomColumns } from './AssetTableHelpers'

interface AssetTableProps {
  siteID: string
  isLoading: boolean
  data: AssetData[]
  initialTableState?: Record<string, unknown>
  onTableStateChange?: (newState: Record<string, unknown>) => void
  // Customization props
  overrideColumns?: ColumnDef<AssetData>[]
  footerName?: string
  routeFrom?: string
  initialSorting?: SortingState
  customTopBar?: (props: {
    columns: ColumnDef<AssetData>[]
    setGlobalFilter: React.Dispatch<unknown>
    getColumn: (columnID: string) => Column<AssetData> | undefined
    initialColumnFilters?: ColumnFiltersState
    navigate: NavigateFn
    disableUrlFilters?: boolean
  }) => React.ReactNode
}

export const AssetTable: React.FC<AssetTableProps> = (props) => {
  const {
    siteID,
    isLoading,
    data,
    initialTableState,
    onTableStateChange,
    overrideColumns,
    footerName = 'Asset(s)',
    routeFrom = '/assets/$siteID',
    initialSorting,
    customTopBar,
  } = props
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate({ from: routeFrom as any })
  const { setAssetVizRenderingMap } = useContext(WebGLContext)

  const site = useGetCustomSiteField(siteID)
  const customFields = useMemo(() => {
    return site.data && site.data[0].entry.data.custom_fields
      ? site.data[0].entry.data.custom_fields
      : []
  }, [site.data])

  const { defaultColumns, hiddenColumns } = AssetTableColumns(siteID)
  const customFieldColumns = getAssetTableCustomColumns(customFields)

  // Use custom columns if provided, otherwise use default columns
  const columns = useMemo(() => {
    if (overrideColumns) {
      return overrideColumns
    }

    // Default behavior: use asset columns with custom fields
    return [...defaultColumns, ...hiddenColumns, ...customFieldColumns]
  }, [overrideColumns, customFieldColumns, defaultColumns, hiddenColumns])

  // Restore local sorting state for the table UI only
  const [sorting, setSorting] = React.useState<SortingState>(
    initialSorting ?? [{ id: 'lastUpdated', desc: true }]
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 24,
  })

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Initialize filters from URL
  useEffect(() => {
    if (!initialTableState) return

    const filters = Object.entries(initialTableState)
      .filter(([key]) => {
        // If custom columns are provided, only allow filters that match column IDs
        if (overrideColumns) {
          const allowedFilters = overrideColumns
            .map((col) => col.id)
            .filter(Boolean)
          return allowedFilters.includes(key)
        }

        // Otherwise, check for standard filters or custom field filters
        const isStandardFilter = (
          Object.values(AssetTableColumn) as string[]
        ).includes(key)

        // Include custom field filters
        const isCustomField = customFields.some((field) => field.key === key)

        return isStandardFilter || isCustomField
      })
      .map(([id, value]) => ({ id, value }))

    setColumnFilters(filters)
  }, [initialTableState, customFields, overrideColumns])

  useEffect(() => {
    setAssetVizRenderingMap(new Map())
  }, [pagination, setAssetVizRenderingMap])

  const globalFilterFn: FilterFn<AssetData> = (row, _, filterValue) => {
    const searchValue =
      typeof filterValue === 'string' ? filterValue.toLowerCase() : filterValue

    const nameMatch = row.original.name.toLowerCase().includes(searchValue)

    const erpIdMatch =
      row.original.erpId?.toLowerCase().includes(searchValue) || false

    return nameMatch || erpIdMatch
  }

  const dateRangeFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    const [startDateStr, endDateStr] = filterValue as [
      string | undefined,
      string | undefined,
    ]
    const rowValue = row.getValue(columnId) as string

    if (!rowValue) return true

    const rowDate = parseISO(rowValue)
    const startDate = startDateStr ? parseISO(startDateStr) : null
    const endDate = endDateStr ? parseISO(endDateStr) : null

    if (endDate) {
      endDate.setHours(23, 59, 59, 999) // set to end of the day
    }

    const isAfterStart = startDate ? compareAsc(rowDate, startDate) >= 0 : true
    const isBeforeEnd = endDate ? compareAsc(rowDate, endDate) <= 0 : true

    return isAfterStart && isBeforeEnd
  }

  const multiSelectFilterFn: FilterFn<unknown> = (
    row,
    columnId,
    filterValue
  ) => {
    const rowValue = row.getValue(columnId)

    // Handle case where filterValue is not an array
    if (!Array.isArray(filterValue)) {
      return false
    }

    const normalizedFilterValues = filterValue.map((v: string) =>
      normalizeString(v)
    )

    const processValue = (value: string) => {
      const normalizedValue = normalizeString(value)
      return normalizedFilterValues.includes(normalizedValue)
    }

    if (typeof rowValue === 'string') {
      return processValue(rowValue)
    } else if (Array.isArray(rowValue)) {
      return rowValue.some(
        (item) => typeof item === 'string' && processValue(item)
      )
    }

    return false
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      customGlobalFilter: globalFilterFn,
      dateRangeFilter: dateRangeFilterFn,
      multiSelectFilter: multiSelectFilterFn,
    },
    autoResetPageIndex: false,
  })

  const handleRowClick = (row: AssetData, event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      const url = `/assets/${siteID}/${row.id}?view=details`
      window.open(url, '_blank')
      return
    }
    event.preventDefault()

    // Get current table state and convert filters to individual parameters
    const currentFilters = table
      .getState()
      .columnFilters.reduce(
        (acc: Record<string, string | string[] | undefined>, filter) => {
          acc[filter.id] = filter.value as string | string[] | undefined
          return acc
        },
        {}
      )

    navigate({
      to: '/assets/$siteID/$unitID',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      from: routeFrom as any,
      params: { siteID, unitID: row.id },
      search: (prev) => ({
        ...prev,
        view: 'details',
        ...currentFilters,
      }),
    })
  }

  const handleFilterChange = (
    filteredData: RowModel<AssetData>,
    globalFilter: unknown,
    columnFilters: ColumnFiltersState
  ) => {
    const filters = columnFilters.reduce(
      (acc: Record<string, string | string[] | undefined>, filter) => {
        acc[filter.id] = filter.value as string | string[] | undefined
        return acc
      },
      {}
    )
    onTableStateChange?.(filters)
  }

  // Calculate default visible columns
  const defaultVisibleColumnIds = useMemo(() => {
    if (overrideColumns) {
      return overrideColumns.map((col) => col.id ?? '').filter(Boolean)
    }
    const customFieldColumns = getAssetTableCustomColumns(customFields)
    return [
      ...defaultColumns.map((col) => col.id ?? ''),
      ...customFieldColumns.map((col) => col.id ?? ''),
    ]
  }, [overrideColumns, customFields, defaultColumns])

  return (
    <TableComponent
      isLoadingData={isLoading}
      data={data}
      columns={columns}
      footerName={footerName}
      sorting={sorting}
      setSorting={setSorting}
      pagination={pagination}
      setPagination={setPagination}
      defaultVisibleColumnIds={defaultVisibleColumnIds}
      onRowClick={(row, event) => {
        handleRowClick(row.original, event)
      }}
      globalFilterFn={globalFilterFn}
      onFilteredDataChange={handleFilterChange}
      urlFilters={
        (initialTableState || {}) as Record<
          string,
          string | number | boolean | string[] | [string | null, string | null]
        >
      }
      customTopBar={customTopBar}
    />
  )
}
